/**
 * O discriminante da recusa de `POST /runs` por FUNCIONALIDADE não contratada
 * — a única classe de recusa que pode degradar para o Modo Chat.
 *
 * ★ **Por que a string, e não um campo do envelope.** A rota devolve essa
 * recusa como `_envelope(403, str(exc))`, SEM `value`
 * (`aawz_intelligence_v2/resources/resource_runs_aceite.py`, nos dois
 * `except _apoio.SemFuncionalidadeError`). E `str(exc)` é
 * `f'[{self.codigo}] {motivo} {contexto}'`
 * (`usecases/agente/_aceitar_run_apoio.py::AceitarRunError.__init__`), com
 * `codigo = 'SEM_FUNCIONALIDADE'`. Ou seja: **o único sinal que chega ao front
 * é o prefixo do código na `message`** — não há `value.codigo` nem
 * `value.motivo` para ler. Verificado nas duas pontas antes de escolher.
 *
 * ⚠️ **Seria mais robusto com um `codigo` no envelope.** O irmão
 * `AgenteNaoSelecionavelError` já manda `{'motivo': ..., 'agent_slug': ...}` em
 * `value` (e é assim que `utils/agentes-selecionaveis.js::recusaDeAgente`
 * decide, sem olhar texto nenhum). Enquanto a recusa por funcionalidade não
 * ganhar o mesmo tratamento, uma reescrita da mensagem no backend quebra este
 * discriminante — e quebra em silêncio, voltando ao defeito de origem. Fica
 * registrado como o próximo passo do lado do servidor.
 *
 * ★ **Precisão nos dois sentidos**, que é o que separa este caso dos outros
 * que NÃO podem degradar:
 *
 * | recusa | envelope | degrada? |
 * |---|---|---|
 * | funcionalidade não contratada | 403 + `[SEM_FUNCIONALIDADE] …` | **sim** |
 * | permissão do usuário (`NaoAutorizadoError`) | 403, mensagem sem código | não |
 * | agente recusado (`AgenteNaoSelecionavelError`) | 400/403 + `[AGENTE_NAO_SELECIONAVEL] …` + `value.motivo` | não |
 * | saldo (`ReservaInsuficienteError`) | 402 | não |
 * | run em andamento | 409 | não |
 * | sem worker | 503 | não |
 *
 * Os dois eixos são exigidos juntos (status **e** prefixo): o status sozinho
 * confundiria com o 403 de permissão, e a string sozinha aceitaria um
 * envelope de outro status que citasse o código por acidente.
 */

/** O código da exceção do backend, como ele chega no início da `message`. */
export const PREFIXO_SEM_FUNCIONALIDADE = '[SEM_FUNCIONALIDADE]'

/** O status HTTP-no-corpo dessa recusa (`SemFuncionalidadeError.http_status`). */
export const STATUS_SEM_FUNCIONALIDADE = 403

/**
 * O envelope recusou o run por funcionalidade não contratada?
 *
 * @param {Object} r Envelope `{status, message, value}` do `POST /runs`.
 * @returns {boolean} `true` só na recusa por FUNCIONALIDADE.
 */
export function ehRecusaPorFuncionalidade(r) {
  if (!r || r.status !== STATUS_SEM_FUNCIONALIDADE) return false
  const mensagem = typeof r.message === 'string' ? r.message.trim() : ''
  // `indexOf === 0` (não `contains`): o código abre a mensagem por
  // construção. Aceitar em qualquer posição faria um texto do consultor
  // ecoado de volta virar gatilho de degradação.
  return mensagem.indexOf(PREFIXO_SEM_FUNCIONALIDADE) === 0
}
